import { formatTimeIn } from '../lib/format-fr.ts';
import type { IsoDateTime } from './common.ts';
import type { SourceRef } from './source.ts';

export type WeatherCondition =
  | 'clear'
  | 'partly_cloudy'
  | 'cloudy'
  | 'fog'
  | 'drizzle'
  | 'rain'
  | 'heavy_rain'
  | 'snow'
  | 'sleet'
  | 'freezing_rain'
  | 'thunderstorm'
  | 'unknown';

export interface WeatherObservation {
  readonly observedAt: IsoDateTime;
  /** Mesure d'une station ou sortie de modèle : affiché honnêtement dans l'UI. */
  readonly kind: 'observation' | 'model';
  readonly temperatureC: number;
  readonly feelsLikeC?: number;
  readonly condition: WeatherCondition;
  readonly isDay?: boolean;
  readonly humidityPct?: number;
  readonly windSpeedKmh?: number;
  readonly windGustKmh?: number;
  readonly windDirectionDeg?: number;
  readonly precipitationMm?: number;
  readonly source: SourceRef;
}

export type PrecipitationIntensity = 'none' | 'light' | 'moderate' | 'heavy';

/**
 * Classe d'intensité à partir d'un taux en mm/h. Seuils usuels (American Meteorological Society) :
 * faible < 2,5 mm/h ≤ modérée < 7,6 mm/h ≤ forte. En dessous de 0,1 mm/h : pas de pluie significative.
 */
export function intensityFromRate(mmPerHour: number): PrecipitationIntensity {
  if (mmPerHour < 0.1) return 'none';
  if (mmPerHour < 2.5) return 'light';
  if (mmPerHour < 7.6) return 'moderate';
  return 'heavy';
}

export interface PrecipitationStep {
  readonly start: IsoDateTime;
  readonly end: IsoDateTime;
  readonly intensity: PrecipitationIntensity;
}

export interface PrecipitationNowcast {
  readonly issuedAt: IsoDateTime;
  readonly steps: readonly PrecipitationStep[];
  readonly source: SourceRef;
}

export type NowcastSummary =
  | { readonly kind: 'dry'; readonly horizonMinutes: number }
  | { readonly kind: 'rain_expected'; readonly inMinutes: number; readonly intensity: PrecipitationIntensity }
  | { readonly kind: 'raining'; readonly intensity: PrecipitationIntensity; readonly untilAt: IsoDateTime | null }
  | { readonly kind: 'unknown' };

/**
 * Résume la prévision de pluie pour les `horizonMinutes` à venir.
 * Seuls les pas dont la fin est postérieure à `now` sont pris en compte.
 */
export function summarizeNowcast(
  nowcast: Pick<PrecipitationNowcast, 'steps'>,
  now: Date,
  horizonMinutes = 60,
): NowcastSummary {
  const t = now.getTime();
  const horizon = t + horizonMinutes * 60_000;
  const steps = nowcast.steps
    .filter((s) => Date.parse(s.end) > t && Date.parse(s.start) < horizon)
    .toSorted((a, b) => Date.parse(a.start) - Date.parse(b.start));
  const first = steps[0];
  if (first === undefined) return { kind: 'unknown' };

  if (first.intensity !== 'none' && Date.parse(first.start) <= t) {
    const firstDry = steps.find((s) => s.intensity === 'none');
    return { kind: 'raining', intensity: first.intensity, untilAt: firstDry ? firstDry.start : null };
  }
  const wet = steps.find((s) => s.intensity !== 'none');
  if (wet === undefined) return { kind: 'dry', horizonMinutes };
  return {
    kind: 'rain_expected',
    inMinutes: Math.max(0, Math.round((Date.parse(wet.start) - t) / 60_000)),
    intensity: wet.intensity,
  };
}

/**
 * Libellés français des conditions et de la pluie.
 *
 * Ils vivent ici, et non dans l'interface, parce que le Worker s'en sert pour écrire le contenu HTML servi
 * aux robots : les moteurs génératifs n'exécutent pas JavaScript, et le texte qu'ils lisent doit être celui
 * que le visiteur voit.
 */
export const WEATHER_CONDITION_LABELS: Readonly<Record<WeatherCondition, string>> = {
  clear: 'Ciel dégagé',
  partly_cloudy: 'Éclaircies',
  cloudy: 'Nuageux',
  fog: 'Brouillard',
  drizzle: 'Bruine',
  rain: 'Pluie',
  heavy_rain: 'Forte pluie',
  snow: 'Neige',
  sleet: 'Pluie et neige',
  freezing_rain: 'Pluie verglaçante',
  thunderstorm: 'Orage',
  unknown: 'Conditions inconnues',
};

const INTENSITY_LABELS: Readonly<Record<PrecipitationIntensity, string>> = {
  none: 'pas de pluie',
  light: 'pluie faible',
  moderate: 'pluie modérée',
  heavy: 'forte pluie',
};

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Texte court de la prévision de pluie. Le « ~ » rappelle qu'il s'agit d'une prévision. */
export function nowcastText(summary: NowcastSummary, timeZone: string): string {
  switch (summary.kind) {
    case 'dry':
      return `Pas de pluie prévue d’ici ${summary.horizonMinutes} min`;
    case 'rain_expected':
      return summary.inMinutes <= 5
        ? `${capitalize(INTENSITY_LABELS[summary.intensity])} imminente`
        : `${capitalize(INTENSITY_LABELS[summary.intensity])} dans ~${summary.inMinutes} min`;
    case 'raining':
      return summary.untilAt === null
        ? `${capitalize(INTENSITY_LABELS[summary.intensity])} en cours`
        : `${capitalize(INTENSITY_LABELS[summary.intensity])}, fin vers ${formatTimeIn(summary.untilAt, timeZone)}`;
    case 'unknown':
      return 'Prévision indisponible';
  }
}
